import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Users, BarChart3, Settings, Zap, Home, Calendar, MessageSquare, Star, BookOpen, Navigation, LogOut,
  DollarSign, Banknote, Scale, Shield, Palette, Radio,
} from 'lucide-react';
import { useAuthStore } from '../stores/authStore';
import { getRoleConfig, IconName } from '../config/roleConfig';

// Map icon names to icon components
const ICON_MAP: Record<IconName, React.ReactNode> = {
  Users: <Users size={20} />,
  BarChart3: <BarChart3 size={20} />,
  Settings: <Settings size={20} />,
  Zap: <Zap size={20} />,
  Home: <Home size={20} />,
  Calendar: <Calendar size={20} />,
  MessageSquare: <MessageSquare size={20} />,
  Star: <Star size={20} />,
  BookOpen: <BookOpen size={20} />,
  Navigation: <Navigation size={20} />,
  LogOut: <LogOut size={20} />,
  DollarSign: <DollarSign size={20} />,
  Banknote: <Banknote size={20} />,
  Scale: <Scale size={20} />,
  Shield: <Shield size={20} />,
  Palette: <Palette size={20} />,
  Radio: <Radio size={20} />,
};

interface SidebarProps {
  onClose?: () => void;
}

export const Sidebar: React.FC<SidebarProps> = ({ onClose }) => {
  const location = useLocation();
  const { user } = useAuthStore();
  const userRole = user?.roles?.[0];

  if (!userRole) {
    return null;
  }

  const roleConfig = getRoleConfig(userRole as import('../types').UserRole);
  const menuItems = roleConfig.menuItems.filter((item) => item.visible);

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  return (
    <aside className="flex h-full w-64 flex-col border-r border-gray-200 bg-white">
      {/* Logo Section */}
      <div className="border-b border-gray-200 px-6 py-6">
        <h2 className="text-2xl font-bold text-primary-600">BookBed</h2>
        <p className="mt-1 text-xs text-gray-500">{roleConfig.displayName}</p>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-4 py-6">
        <ul className="space-y-2">
          {menuItems.map((item) => (
            <li key={item.id}>
              <Link
                to={item.path}
                onClick={onClose}
                className={`flex items-center gap-3 rounded-lg px-4 py-3 text-sm font-medium transition-colors ${
                  isActive(item.path)
                    ? 'bg-primary-50 text-primary-700'
                    : 'text-gray-700 hover:bg-gray-50'
                }`}
              >
                {/* text-primary-700, not -600 -- -600 (#ED1148) on bg-primary-50 (#FFF1F2) is a
                    4.0:1 contrast ratio, under WCAG 2 AA's 4.5:1 minimum for normal text. Found
                    via axe-core on the fixed-tier (Super Admin / Platform Admin / Platform Staff)
                    sidebar nav's active-item state. */}
                <span className={isActive(item.path) ? 'text-primary-700' : 'text-gray-400'}>
                  {ICON_MAP[item.iconName]}
                </span>
                <span>{item.label}</span>
              </Link>
            </li>
          ))}
        </ul>
      </nav>

      {/* Footer Info */}
      <div className="border-t border-gray-200 px-6 py-4">
        <p className="text-xs text-gray-500">
          Version 1.0 <br /> © 2026 BookBed
        </p>
      </div>
    </aside>
  );
};
